import type { ReactNode } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import useTracksStore from '../../shared/store/tracks.store.tsx'
import { useShallow } from 'zustand/react/shallow'

interface DraggerProps {
  children: ReactNode
  onDragStart: () => void
}

const Dragger: React.FC<DraggerProps> = ({ children, onDragStart }) => {
  const [isDragging, setIsDragging] = useState(false)
  const dragElementRef = useRef<HTMLDivElement | null>(null)
  const cloneRef = useRef<HTMLDivElement | null>(null)
  const initialPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const offset = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const { setDraggedClip, draggedClip } = useTracksStore(
    useShallow((state) => ({
      setDraggedClip: state.setDraggedClip,
      draggedClip: state.draggedClip,
    }))
  )
  const draggingClipRef = useRef(draggedClip)
  const hasStartedDragging = useRef(false)

  useEffect(() => {
    draggingClipRef.current = draggedClip
  }, [draggedClip])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (['BUTTON', 'svg', 'path'].includes((e.target as HTMLElement).tagName))
        return

      const element = dragElementRef.current
      if (!element) return

      const rect = element.getBoundingClientRect()
      initialPos.current = { x: e.clientX, y: e.clientY }
      offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }

      hasStartedDragging.current = false

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const dx = moveEvent.clientX - initialPos.current.x
        const dy = moveEvent.clientY - initialPos.current.y
        const movedEnough = Math.abs(dx) > 5 || Math.abs(dy) > 5

        if (!hasStartedDragging.current && movedEnough) {
          hasStartedDragging.current = true

          document.body.style.userSelect = 'none'

          const clone = element.cloneNode(true) as HTMLDivElement
          const rect = element.getBoundingClientRect()

          clone.style.position = 'absolute'
          clone.style.pointerEvents = 'none'
          clone.style.opacity = '1'
          clone.style.zIndex = '9999'
          clone.style.left = `${moveEvent.clientX - offset.current.x}px`
          clone.style.top = `${moveEvent.clientY - offset.current.y}px`
          clone.style.width = `${rect.width}px`
          clone.style.height = `${rect.height}px`
          clone.classList.add('shadow-lg')

          document.body.appendChild(clone)
          onDragStart()

          cloneRef.current = clone
          setIsDragging(true)
        }

        if (hasStartedDragging.current && cloneRef.current) {
          if (draggingClipRef.current?.trackId !== 'tmp') {
            cloneRef.current.style.opacity = '0'
          } else {
            cloneRef.current.style.opacity = '1'
            cloneRef.current.style.left = `${moveEvent.clientX - offset.current.x}px`
            cloneRef.current.style.top = `${moveEvent.clientY - offset.current.y}px`
          }
        }
      }

      const handleMouseUp = () => {
        if (cloneRef.current) {
          document.body.removeChild(cloneRef.current)
          cloneRef.current = null
        }

        document.body.style.userSelect = ''
        setIsDragging(false)
        setDraggedClip(undefined)

        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [onDragStart, setDraggedClip]
  )

  return (
    <div
      ref={dragElementRef}
      onMouseDown={handleMouseDown}
      className={`relative ${isDragging ? 'opacity-50' : 'opacity-100'}`}
    >
      <div className="flex h-full">
        <div className="flex h-auto min-w-8 cursor-move items-center justify-center rounded-l-sm bg-zinc-600">
          <div className="flex flex-col items-center gap-1">
            <div className="h-1 w-1 rounded-full bg-black" />
            <div className="h-1 w-1 rounded-full bg-black" />
            <div className="h-1 w-1 rounded-full bg-black" />
          </div>
        </div>
        <div className="flex h-full grow flex-col gap-3">{children}</div>
      </div>
    </div>
  )
}

export default React.memo(Dragger)
